import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/Avatar";
import { RoleBadge } from "@/components/badges";
import { CompanyCard } from "@/components/CompanyCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Clock, Copy, Loader2, Star, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";

const offsetMinutes = (iana, date) => {
  try {
    const local = new Date(date.toLocaleString("en-US", { timeZone: iana }));
    return Math.round((local.getTime() - date.getTime()) / 60000);
  } catch (e) {
    return null;
  }
};

const localTime = (iana, date) => {
  try {
    return new Intl.DateTimeFormat("en-GB", { timeZone: iana, hour: "2-digit", minute: "2-digit" }).format(date);
  } catch (e) {
    return null;
  }
};

function PersonCard({ member, viewerTz, now, canManage, onToggleManager }) {
  const { t: tr } = useTranslation();
  const tz = member.timezone;
  const t = tz ? localTime(tz, now) : null;
  let diffLabel = "";
  if (tz && viewerTz) {
    const a = offsetMinutes(tz, now);
    const b = offsetMinutes(viewerTz, now);
    if (a != null && b != null) {
      const diff = a - b;
      if (diff === 0) {
        diffLabel = tr("members.timeSame");
      } else {
        const hrs = Math.abs(diff) / 60;
        const hrsStr = Number.isInteger(hrs) ? `${hrs}` : hrs.toFixed(1);
        diffLabel = diff > 0 ? tr("members.timeAhead", { hours: hrsStr }) : tr("members.timeBehind", { hours: hrsStr });
      }
    }
  }
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-white p-3 shadow-sm" data-testid={`member-card-${member.id}`}>
      <UserAvatar user={member} size="h-11 w-11" textSize="text-base" testId={`member-avatar-${member.id}`} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {member.name}
          {member.is_manager && <Badge variant="outline" className="ml-1.5 bg-amber-50 font-mono text-[9px] text-amber-700" data-testid={`member-manager-badge-${member.membership_id}`}>{tr("members.manager")}</Badge>}
        </div>
        <div className="truncate text-xs text-slate-500">{member.job_title || member.email}</div>
        <div className="mt-1 flex items-center gap-1 font-mono text-[11px] text-slate-500" data-testid={`member-time-${member.id}`}>
          <Clock className="h-3 w-3 shrink-0" />
          {t ? <span>{t} {tr("members.local")}{diffLabel ? ` · ${diffLabel}` : ""}</span> : <span className="italic">{tr("members.tzNotSet")}</span>}
        </div>
      </div>
      {canManage && member.membership_id && (
        <button
          type="button"
          title={member.is_manager ? tr("members.unsetManagerT") : tr("members.setManagerT")}
          onClick={() => onToggleManager(member)}
          className={`shrink-0 rounded p-1 transition-colors ${member.is_manager ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`}
          data-testid={`member-manager-toggle-${member.membership_id}`}
        >
          <Star className="h-4 w-4" fill={member.is_manager ? "currentColor" : "none"} />
        </button>
      )}
    </div>
  );
}

export default function Members() {
  const { selectedId } = useProject();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const [selectedParty, setSelectedParty] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["project-members", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/members`)).data,
    enabled: !!selectedId,
  });

  const viewerTz = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const qc = useQueryClient();
  const canManage = !!(user?.is_super_admin || user?.is_admin);
  const managerMut = useMutation({
    mutationFn: async ({ mid, is_manager }) => api.put(`/admin/memberships/${mid}/manager`, { is_manager }),
    onSuccess: () => { toast.success(t("members.managerToast")); qc.invalidateQueries({ queryKey: ["project-members", selectedId] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const [inviteParty, setInviteParty] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "" });
  const [inviteLink, setInviteLink] = useState("");
  const inviteMut = useMutation({
    mutationFn: async () => (await api.post(`/projects/${selectedId}/team-invites`, inviteForm)).data,
    onSuccess: (d) => { toast.success(t("members.inviteSentToast")); setInviteLink(d.link); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const { roots, childrenMap, membersByParty } = useMemo(() => {
    const parties = data?.parties || [];
    const ids = new Set(parties.map((p) => p.id));
    const cMap = new Map();
    const rootList = [];
    for (const p of parties) {
      if (p.parent_party_id && ids.has(p.parent_party_id) && p.parent_party_id !== p.id) {
        cMap.set(p.parent_party_id, [...(cMap.get(p.parent_party_id) || []), p]);
      } else {
        rootList.push(p);
      }
    }
    const isClient = (p) => (p.role || "").trim().toLowerCase() === "client";
    rootList.sort((a, b) => (isClient(b) ? 1 : 0) - (isClient(a) ? 1 : 0) || a.company_name.localeCompare(b.company_name));
    for (const arr of cMap.values()) arr.sort((a, b) => a.company_name.localeCompare(b.company_name));
    const mMap = new Map();
    for (const m of data?.members || []) {
      mMap.set(m.party_id, [...(mMap.get(m.party_id) || []), m]);
    }
    for (const arr of mMap.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    return { roots: rootList, childrenMap: cMap, membersByParty: mMap };
  }, [data]);

  // Recursive render FUNCTION (not a recursive component) — safe for the visual-edits babel plugin.
  const renderParty = (party, depth) => {
    const members = membersByParty.get(party.id) || [];
    const children = childrenMap.get(party.id) || [];
    return (
      <div
        key={party.id}
        className={`rounded-lg border p-3 sm:p-4 ${depth === 0 ? "border-slate-300 bg-white shadow-sm" : "border-slate-200 bg-slate-50/60"}`}
        data-testid={`party-node-${party.id}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSelectedParty(party)}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-sky-200/80 bg-sky-50 px-2.5 py-1 font-mono text-xs font-bold tracking-wider text-sky-900 transition-all duration-150 hover:border-sky-300 hover:bg-sky-100 hover:shadow-xs focus-visible:ring-2 focus-visible:ring-sky-500"
            data-testid={`company-badge-${party.company?.code || party.id}`}
            title={t("companyCard.openCard")}
          >
            <Building2 className="h-3.5 w-3.5" />
            {party.company?.code || party.company_name}
          </button>
          <span className="font-display text-base font-bold text-slate-900">{party.company_name}</span>
          <RoleBadge role={party.role} />
          {members.some((m) => m.id === user?.id && m.is_manager) && (
            <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={() => { setInviteParty(party); setInviteForm({ email: "", name: "" }); setInviteLink(""); }} data-testid={`invite-teammate-${party.id}`}>
              <UserPlus className="mr-1 h-3.5 w-3.5" /> {t("members.inviteTeammate")}
            </Button>
          )}
        </div>
        {members.length === 0 ? (
          <p className="mt-2 text-xs italic text-slate-400">{t("members.noMembers")}</p>
        ) : (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {members.map((m) => (
              <PersonCard key={m.id} member={m} viewerTz={viewerTz} now={now} canManage={canManage} onToggleManager={(mem) => managerMut.mutate({ mid: mem.membership_id, is_manager: !mem.is_manager })} />
            ))}
          </div>
        )}
        {children.length > 0 && (
          <div className="mt-3 space-y-3 border-l-2 border-slate-200 pl-3 sm:pl-4">
            {children.map((c) => renderParty(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">{t("members.title")}</h1>
        <p className="text-sm text-slate-500">{t("members.subtitle")}</p>
      </div>
      {isLoading && <p className="text-sm text-slate-500" data-testid="members-loading">{t("common.loading")}</p>}
      {data && roots.length === 0 && <p className="text-sm text-slate-500" data-testid="members-empty">{t("members.empty")}</p>}
      {roots.length > 0 && <div className="space-y-4">{roots.map((p) => renderParty(p, 0))}</div>}
      <Dialog open={!!inviteParty} onOpenChange={(o) => { if (!o) { setInviteParty(null); setInviteLink(""); } }}>
        <DialogContent data-testid="teammate-invite-dialog">
          <DialogHeader><DialogTitle>{t("members.inviteTeammateTitle", { company: inviteParty?.company_name })}</DialogTitle></DialogHeader>
          {inviteLink ? (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">{t("members.inviteSentToast")}</p>
              <div className="flex items-center gap-2 overflow-hidden rounded-md border bg-slate-50 px-2 py-1.5">
                <span className="flex-1 truncate font-mono text-xs" title={inviteLink} data-testid="teammate-invite-link">{inviteLink}</span>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(inviteLink).then(() => toast.success(t("members.linkCopiedToast"))).catch(() => {}); }} data-testid="teammate-invite-copy">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("common.email")}</Label>
                <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="teammate-invite-email" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.name")}</Label>
                <Input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} data-testid="teammate-invite-name" />
              </div>
            </div>
          )}
          <DialogFooter>
            {!inviteLink && (
              <Button onClick={() => inviteMut.mutate()} disabled={!inviteForm.email.trim() || inviteMut.isPending} data-testid="teammate-invite-submit">
                {inviteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("members.inviteTeammate")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CompanyCard
        party={selectedParty}
        members={selectedParty ? membersByParty.get(selectedParty.id) || [] : []}
        open={!!selectedParty}
        onClose={() => setSelectedParty(null)}
      />
    </div>
  );
}
